import { Factory } from "@/data/dao/Factory";
import { AuditDao } from "@/data/dao/service/AuditDao";
import type { IActivity } from "@/data/model/Audit";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { TastierGlobal } from "@/portal/globalAccess";
import { UiNotification } from "@/portal/ui/Notification";
import { PortalDateTime } from "@/portal/util/PortalDateTime";

export class ActivityFeed {
    private $element: JQuery;
    private $body: JQuery;
    private currentOffset = 0;
    private entriesEachLoad = 20;
    private isLoading = false;

    constructor($portlet: JQuery) {
        this.$element = $portlet;

        this.$body = this.$element.find("> .kt-portlet__body > .kt-widget3");
        this.load();

        TastierGlobal.getKTUtil().scrollInit(this.$body[0], {
            mobileNativeScroll: true,
            resetHeightOnDestroy: true,
            handleWindowResize: true,
            height: () => {
                return this.$body.parent().height();
            },
        });

        this.$body.bind("ps-y-reach-end", () => {
            this.load();
        });
    }

    private load() {
        if (this.isLoading) return;

        const loader = $(
            '<div class="loader"><div class="kt-spinner kt-spinner--lg kt-spinner--success"></div>' +
                i18n("activity.loadingActivities") +
                "</div>"
        ).appendTo(this.$body);
        this.isLoading = true;

        Factory.getInstance<AuditDao>(AuditDao)
            .getData({
                offset: this.currentOffset,
                limit: this.entriesEachLoad,
            })
            .then((activities) => {
                loader.remove();

                this.currentOffset += activities.items.length;
                if (!activities.hasNext) this.unbindLazyLoading();

                if (activities.items.length === 0) {
                    this.$body.append(
                        '<div class="kt-widget3__item"><div class="kt-widget3__body"><p class="kt-widget3__text">' +
                            i18n("activity.noActivities") +
                            "</div></div></div>"
                    );
                } else {
                    for (const i in activities.items) {
                        const act = activities.items[i];
                        this.renderEntry(act).appendTo(this.$body);
                    }
                }

                this.isLoading = false;
            })
            .catch((ex) => {
                logError(ex);
                loader.remove();
                UiNotification.showSuccess(i18n("activity.failedLoading"));
                this.isLoading = false;
            });
    }

    private unbindLazyLoading() {
        this.$body.unbind("ps-y-reach-end");
    }

    private renderEntry(model: IActivity): JQuery {
        const item = $('<div class="kt-widget3__item" />');

        const header = $('<div class="kt-widget3__header" />').appendTo(item);
        if (model.userId !== undefined && (model.userAvatar !== undefined || model.userInitials !== undefined))
            header.append(
                '<div class="kt-widget3__user-img">' +
                    (model.userAvatar !== undefined
                        ? '<img class="kt-widget3__img" src="' + model.userAvatar + '" />'
                        : '<div class="kt-badge kt-badge--lg kt-badge--success">' + model.userInitials + "</div>") +
                    "</div>"
            );

        const headerInfo = $('<div class="kt-widget3__info" />').appendTo(header);

        if (model.userId !== undefined && model.userName !== undefined)
            headerInfo.append(
                '<a href="/user/' +
                    model.userId +
                    '" class="kt-widget3__username">' +
                    Format.htmlEscape(model.userName) +
                    "</a><br />"
            );
        else headerInfo.append('<div class="kt-widget3__deleteduser">' + i18n("general.deletedUser") + "</div>");

        header.append('<div class="kt-widget3__status kt-font-info" />');

        headerInfo.append(
            ' <span class="kt-widget3__time">' + PortalDateTime.toRelativeString(model.created) + "</span>"
        );
        item.append('<div class="kt-widget3__body"><p class="kt-widget3__text">' + model.message + "</p></div>");

        return item;
    }

    public appendTo(el: JQuery): ActivityFeed {
        el.append(this.$element);

        return this;
    }
}
